import * as React from "react";
import * as yup from "yup";

import {
  DocumentDto,
  EmployeeDepartmentDto,
  EmployeeDto,
  EmployeePositionDto,
  EmployeeTypeCategoryID,
  EmployeeTypeID,
} from "Api/Api";
import { Resources, useResource } from "Translations/Resources";

import { EditDocuments } from "Components/Shared/Documents/EditDocuments";
import { EmployeeEditBasicInfo } from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditBasicInfo/EmployeeEditBasicInfo";
import { EmployeeEditDepartments } from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditDepartments";
import { EmployeeEditHelpdeskWriting } from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditHelpdeskWriting";
import { EmployeeEditCanShowTicketsForColleaguesFromOwnDepartments } from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditCanShowTicketsForColleaguesFromOwnDepartments";
import { EmployeeEditPositions } from "Components/HumanResources/Employees/EmployeeEdit/EmployeeEditPositions/EmployeeEditPositions";
import { PrimaryButton } from "Components/Shared/Buttons/PrimaryButton";
import { cloneDeep } from "lodash";
import useAutoAdder from "Hooks/useAutoAdder";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers";

type Props = {
  employee: EmployeeDto;
  isLoading: boolean;
  onSave: (employeeToSave: EmployeeDto) => void;
};

type FormData = Pick<
  EmployeeDto,
  | "firstName"
  | "lastName"
  | "email"
  | "phone"
  | "positions"
  | "departments"
  | "supervisorID"
  | "supervisorFirstName"
  | "supervisorLastName"
  | "documents"
  | "anniversaryDate"
  | "birthDate"
  | "isHelpdeskWritingDisabled"
  | "isActive"
  | "employeeTypeID"
  | "employeeTypeCategoryID"
  | "canShowTicketsForColleaguesFromOwnDepartments"
> & {
  companyID?: string;
  companyName?: string | null;
};
export type EmployeeFormData = FormData;

export const EmployeeEditForm: React.FunctionComponent<Props> = props => {
  const { employee, onSave } = props;
  const { t } = useResource();

  const requiredString = (validationMessage: string) =>
    yup.string().defined().required(validationMessage);

  const validationSchema: yup.ObjectSchema<FormData> = yup
    .object({
      firstName: requiredString(
        t(Resources.HumanResources.EmployeeEdit.FirstName.Validation),
      ),
      lastName: requiredString(
        t(Resources.HumanResources.EmployeeEdit.LastName.Validation),
      ),
      email: yup
        .string()
        .defined()
        .required(t(Resources.HumanResources.EmployeeEdit.Email.Validation))
        .email(t(Resources.HumanResources.EmployeeEdit.Email.WrongFormat)),
      phone: requiredString(
        t(Resources.HumanResources.EmployeeEdit.Phone.Validation),
      ).matches(
        /^[+]*[0-9]{3} [0-9]{3} [0-9]{3} [0-9]{3}/g,
        t(Resources.HumanResources.EmployeeEdit.Phone.WrongFormat),
      ),
      positions: yup
        .array<EmployeePositionDto>()
        .defined()
        .required(
          t(Resources.HumanResources.EmployeeEdit.Positions.Validation),
        ),
      departments: yup.array<EmployeeDepartmentDto>().defined(),
      supervisorFirstName: yup.string().optional(),
      supervisorLastName: yup.string().optional(),
      documents: yup.array<DocumentDto>().defined(),
      anniversaryDate: yup.string().defined(),
      birthDate: yup.string().defined(),
      companyID: yup.string().when("employeeTypeCategoryID", {
        is: EmployeeTypeCategoryID.External,
        then: yup.string().required("Zadejte společnost"),
        otherwise: yup.string().optional().nullable(),
      }),
      companyName: yup.string().optional().nullable(),
      employeeTypeID: yup
        .string()
        .oneOf(Object.values(EmployeeTypeID))
        .defined(),
      employeeTypeCategoryID: yup
        .string()
        .oneOf(Object.values(EmployeeTypeCategoryID))
        .defined(),
      isHelpdeskWritingDisabled: yup.boolean().defined(),
      canShowTicketsForColleaguesFromOwnDepartments: yup.boolean().defined(),
      isActive: yup.boolean().defined(),
    })
    .defined();

  const getDefaultValues = React.useCallback(
    () => ({
      firstName: employee.firstName,
      lastName: employee.lastName,
      email: employee.email,
      phone: employee.phone ?? "",
      positions: cloneDeep(employee.positions),
      departments: cloneDeep(employee.departments),
      supervisorID: employee.supervisorID ?? undefined,
      companyID: employee.companyID?.toString() ?? undefined,
      companyName: employee.companyName,
      employeeTypeID: employee.employeeTypeID,
      employeeTypeCategoryID: employee.employeeTypeCategoryID,
      supervisorFirstName: employee.supervisorFirstName ?? "",
      supervisorLastName: employee.supervisorLastName ?? "",
      documents: cloneDeep(employee.documents) ?? [],
      anniversaryDate: employee.anniversaryDate ?? "",
      birthDate: employee.birthDate ?? "",
      isHelpdeskWritingDisabled: employee.isHelpdeskWritingDisabled ?? false,
      canShowTicketsForColleaguesFromOwnDepartments:
        employee.canShowTicketsForColleaguesFromOwnDepartments ?? false,
      isActive: employee.isActive ?? true,
    }),
    [employee],
  );

  const form = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: getDefaultValues(),
  });
  const { reset, watch } = form;
  const { ...formValues } = watch();

  const autoAdder = useAutoAdder();

  React.useEffect(() => {
    reset(getDefaultValues());
  }, [employee, getDefaultValues, reset]);

  const onSubmit = (data: FormData) => {
    if (!autoAdder.checkAutoAdd()) {
      return;
    }

    const employeeToSave = {
      ...employee,
      ...data,
      companyID: (formValues.companyID as unknown) as number,
      companyName: formValues.companyName ?? undefined,
    };
    onSave(employeeToSave);
  };

  const onSubmitCapture = () => {
    autoAdder.checkAutoAdd();
  };

  return (
    <form
      onSubmitCapture={e => onSubmitCapture()}
      onSubmit={form.handleSubmit(onSubmit)}
    >
      <EmployeeEditBasicInfo form={form} />

      <EmployeeEditPositions
        form={form}
        employee={employee}
        autoAdderRegister={autoAdder.registerOperation}
      />

      <EmployeeEditDepartments
        form={form}
        autoAdderRegister={autoAdder.registerOperation}
      />

      <EmployeeEditHelpdeskWriting form={form} />

      <EmployeeEditCanShowTicketsForColleaguesFromOwnDepartments form={form} />

      <EditDocuments
        title={t(Resources.HumanResources.EmployeeEdit.Documents.Label)}
        isCompany={false}
        autoAdderRegister={autoAdder.registerOperation}
        form={form}
      />

      <PrimaryButton type={"submit"}>
        {t(Resources.HumanResources.EmployeeEdit.Save)}
      </PrimaryButton>
    </form>
  );
};
